<template>
  <div>
    <NuxtLayout name="default">
      <!-- hero section -->
      <section
        class="bg-image pb-space-lg py-md-space-lg pb-lg-space-xl"
        id="newsroom-hero-section"
      >
        <div class="container px-0 px-lg-space-xs">
          <div class="row no-gutters m-0">
            <div class="col-12 p-0">
              <h1
                class="d-lg-inline-block h1-small bg-secondary px-space-xs py-space-xxs mb-lg-space-lg"
              >
                {{ newsroomHomeData.title }}
              </h1>
              <!-- passing in articles sans text to the carousel; using slot for text -->
              <CarouselImageApollo
                :items="carouselArticles.map(({ text, ...item }) => item)"
                ctaTextSize="small"
                titleLevel="h2"
                titleSize="medium"
                card-padding-y="sm"
                active-indicator-variant="secondary"
                @ctaClick="
                  handleCarouselCtaClick(
                    $event,
                    newsroomHomeData.title!,
                    'Keep reading'
                  )
                "
                @previousClicked="() => onCarouselChange(newsroomHomeData.title!, 'Left chevron')"
                @nextClicked="() => onCarouselChange(newsroomHomeData.title!, 'Right chevron')"
              >
                <!-- using article text via slot in order to avoid component's styling -->
                <template
                  #[`slide(${1+index})`]
                  v-for="(carouselArticle, index) in carouselArticles"
                >
                  <div v-html="carouselArticle.text"></div>
                </template>
              </CarouselImageApollo>
            </div>
          </div>
        </div>
      </section>

      <!-- recent news articles section -->
      <section class="bg-light-2 py-space-lg py-lg-space-xxl">
        <div class="container">
          <div class="row">
            <div class="col-12">
              <h2 class="h2-medium mb-space-sm">
                {{ newsroomHomeData.recentArticlesTitle }}
              </h2>
              <div class="bg-secondary title-underline mb-space-md"></div>
            </div>
          </div>
          <div class="row row-cols-1 row-cols-md-2 row-cols-lg-3">
            <div
              class="col mb-space-sm"
              v-for="article in newsArticles"
              :key="article.id"
            >
              <CardImageArticle
                class="h-100"
                :clickable-card="true"
                border-variant="light-4"
                :image-source="article.coverImage!"
                :image-alt="article.title!"
                :title="article.title!"
                :date="formatDate(article.updatedDate!)"
                :minutes="article.timeToRead!"
                :cta-link="article.detailPage!"
                @cardClick="
                  handleCardArticleClick(
                    $event,
                    newsroomHomeData.recentArticlesTitle!,
                    article.title!,
                    article.detailPage!
                  )
                "
                @ctaClick="
                  handleCardArticleClick(
                    $event,
                    newsroomHomeData.recentArticlesTitle!,
                    article.title!,
                    article.detailPage!
                  )
                "
                @cardEnter="handleEnterKeyEvent(article.detailPage!)"
              >
              </CardImageArticle>
            </div>
          </div>
        </div>
      </section>

      <!-- asu in the news section -->
      <section class="bg-white py-space-lg py-lg-space-xxl">
        <div class="container">
          <div class="row">
            <div class="col-12">
              <h2 class="h2-medium mb-space-sm">
                {{ newsroomHomeData.asuNewsTitle }}
              </h2>
              <div class="bg-secondary title-underline mb-space-md"></div>
            </div>
          </div>
          <div class="row">
            <div
              class="col-12"
              v-for="(item, index) in newsroomHomeData.asuNewsArticles"
              :key="item?.id"
            >
              <div class="d-lg-flex align-items-center mb-space-xs fs-large">
                <div class="mb-space-xs mb-lg-0">
                  <NuxtLink
                    class="asu-news-link fw-bold"
                    :href="item?.uri!"
                    :target="formatLinkTarget(item?.target!)"
                    @click.native="
                      handleInTheNewsClick(
                        newsroomHomeData.asuNewsTitle!,
                        item?.text!
                      )
                    "
                    >{{ item?.text }}
                  </NuxtLink>
                </div>
                <p class="ms-auto">{{ formatDate(item?.publishedDate!) }}</p>
              </div>
              <hr
                v-if="!isLastIndex(newsroomHomeData.asuNewsArticles!, index)"
              />
            </div>
          </div>
        </div>
      </section>

      <!-- collections section -->
      <section
        class="bg-image py-space-lg py-lg-space-xxl newsroom-lazy"
        data-background-image="https://live-asuocms.ws.asu.edu/sites/default/files/UnityUI-NetworkPattern-White%402x.png"
      >
        <div class="container">
          <div class="row">
            <div class="col-12">
              <h2 class="h2-medium mb-space-sm">
                {{ newsroomHomeData.collectionsTitle }}
              </h2>
              <div class="bg-secondary title-underline mb-space-md"></div>
            </div>
          </div>
          <div class="row">
            <div
              class="col-12 col-md-6 col-lg-3 mb-space-sm"
              v-for="card in newsCategories"
              :key="card.id"
            >
              <CardImageTile
                :bg-image-src="card.image!"
                :clickable-card="true"
                :title="card.title!"
                :text="card.cardDescription!"
                text-size="large"
                cta-text="View all articles"
                :cta-link="card.detailPage!"
                :display-cta-button="true"
                cta-button-mobile-variant="secondary"
                cta-button-desktop-variant="secondary"
                @cardClick="
                  handleCardInfoClick(
                    $event,
                    card.title!,
                    'view all articles',
                    'hover card',
                    card.detailPage!
                  )
                "
                @ctaClick="
                  handleCardInfoClick(
                    $event,
                    card.title!,
                    'view all articles',
                    'hover card',
                    card.detailPage!
                  )
                "
                @cardEnter="handleEnterKeyEvent(card.detailPage!)"
              ></CardImageTile>
            </div>
          </div>
        </div>
      </section>

      <!-- social media section -->
      <section
        class="pt-space-lg py-md-space-lg py-lg-space-xl mt-space-lg my-md-space-lg my-lg-space-xxl"
      >
        <div class="container text-white bg-dark-3">
          <div class="row">
            <div
              class="col-12 d-flex d-lg-none justify-content-center social-media-image-div position-relative"
            >
              <img
                class="social-media-image"
                :alt="newsroomHomeData.socialMediaImageAlt!"
                :src="newsroomHomeData.socialMediaImage!"
              />
            </div>
            <div
              class="col-lg-6 d-none d-lg-flex justify-content-center position-relative"
            >
              <div class="social-media-image-div">
                <img
                  class="social-media-image newsroom-lazy"
                  :alt="newsroomHomeData.socialMediaImageAlt!"
                  :data-src="newsroomHomeData.socialMediaImage"
                />
              </div>
            </div>
            <div class="col-12 col-lg-6 px-space-md py-lg-space-xl">
              <h2 class="h2-medium mb-space-sm">
                {{ newsroomHomeData.socialMediaTitle }}
              </h2>
              <div
                class="fs-large mb-space-sm"
                v-html="newsroomHomeData.socialMediaDescription"
              ></div>
              <div
                class="d-flex flex-row flex-wrap justify-content-left mb-space-xl mb-lg-0"
              >
                <NuxtLink
                  v-for="(item, index) in newsroomHomeData.socialMediaLinks!"
                  :key="item!.id"
                  :class="{
                  'me-space-xs': !isLastIndex(newsroomHomeData.socialMediaLinks!, index),
                }"
                  class="social-media-icon"
                  :aria-label="item!.text!"
                  :to="item!.url!"
                  target="_blank"
                  external
                  @click.native="handleSocialMediaLinkClick(item!)"
                >
                  <img :alt="item!.iconAlt!" :src="item!.icon!" />
                </NuxtLink>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- contact asu online section -->
      <section class="bg-light-2 py-space-lg py-lg-space-xxl">
        <div class="container">
          <div class="row">
            <div class="col-12">
              <h2 class="h2-medium mb-space-sm">
                {{ newsroomHomeData.contactTitle }}
              </h2>
              <div class="bg-secondary title-underline mb-space-md"></div>
            </div>
          </div>

          <div class="row row-cols-1 row-cols-md-2">
            <div
              class="col mb-space-sm"
              v-for="item in newsroomHomeData.contactCards"
              :key="item!.id"
            >
              <CardIcon
                class="bg-white h-100"
                border-variant="light-4"
                :icon-source="item!.icon!"
                :icon-alt="item!.title!"
                :title="item!.title!"
                :display-cta="false"
              >
                <div v-html="item!.text"></div>
              </CardIcon>
            </div>
          </div>
        </div>
      </section>

      <SectionRfiMultiStep :bg-image="rfiBgImage"></SectionRfiMultiStep>
    </NuxtLayout>
  </div>
</template>

<script setup lang="ts">
// components
import { CardIcon } from "@rds-vue-ui/card-icon";
import { CardImageArticle } from "@rds-vue-ui/card-image-article";
import { CardImageTile } from "@rds-vue-ui/card-image-tile";
import {
  CarouselImageApollo,
  CarouselItem,
} from "@rds-vue-ui/carousel-image-apollo";
import RfiBgImage from "~/assets/img/rfi-bg-overlay.png";
import SectionRfiMultiStep from "~/components/rfi/SectionRfiMultiStep.vue";

import { analyticsComposable } from "@rds-vue-ui/analytics-gs-composable";
import {
  NewsArticle,
  NewsCategory,
  NewsroomHome,
  ParagraphSocialLink,
} from "~/server/resolvers/resolvers-types";

const config = useRuntimeConfig();
const { $lozad } = useNuxtApp();
const UtilityService = useUtilityService();
const SchemaService = useSchemaService();

onMounted(() => {
  // image lazy loading setup
  const observer = $lozad(".newsroom-lazy");
  observer.observe();
});

const rfiBgImage = RfiBgImage;

// page query setup
interface NewsroomPageQuery {
  newsroomHome: NewsroomHome;
  allNewsArticle: NewsArticle[];
  allNewsCategory: NewsCategory[];
}

const newsroomQuery = `#graphql
  query{
    newsroomHome(id: "c5486e1b-becb-43fa-9777-95075aae1a0c"){
      id
      detailPage
      metaTitle
      metaDescription
      title
      firstFeaturedArticle {
        detailPage
        coverImage
        title
        updatedDate
        timeToRead
        shortDescription
      }
      secondFeaturedArticle {
        detailPage
        coverImage
        title
        updatedDate
        timeToRead
        shortDescription
      }
      thirdFeaturedArticle {
        detailPage
        coverImage
        title
        updatedDate
        timeToRead
        shortDescription
      }
      fourthFeaturedArticle {
        detailPage
        coverImage
        title
        updatedDate
        timeToRead
        shortDescription
      }
      recentArticlesTitle
      asuNewsTitle
      asuNewsArticles (sort: {by: "publishedDate", order: "DESC"}) {
        id
        text
        uri
        target
        publishedDate
      }
      collectionsTitle
      socialMediaTitle
      socialMediaDescription
      socialMediaImage
      socialMediaImageAlt
      socialMediaLinks {
        id
        icon
        iconAlt
        url
        text
      }
      contactTitle
      contactCards {
        id
        icon
        title
        text
      }
    }
    allNewsArticle(sort: {by: "updatedDate", order: "DESC", limit: 6}), {
      detailPage
      coverImage
      title
      publishedDate
      updatedDate
      timeToRead
    }
    allNewsCategory(sort: {by: "title", order: "ASC"}) {
      id
      detailPage
      image
      title
      cardDescription
    }
  }
`;

// query for this page's data
const data = await usePageQuery<NewsroomPageQuery>(
  newsroomQuery,
  {},
  { key: "news-room-home" }
);

// require this page's data
if (
  !(
    data &&
    data.newsroomHome &&
    data.allNewsArticle.length &&
    data.allNewsCategory.length
  )
) {
  throw createError({ message: `${String(useRoute().name)} query failed` });
}

const newsroomHomeData = data.newsroomHome,
  newsArticles = data.allNewsArticle,
  newsCategories = data.allNewsCategory;

// ensure consumed newsroomHomeData is present
if (
  !(
    typeof newsroomHomeData?.title === "string" &&
    typeof newsroomHomeData?.recentArticlesTitle === "string" &&
    typeof newsroomHomeData?.socialMediaImageAlt === "string" &&
    Array.isArray(newsroomHomeData?.asuNewsArticles) &&
    Array.isArray(newsroomHomeData?.socialMediaLinks) &&
    Array.isArray(newsroomHomeData?.contactCards)
  )
) {
  throw createError({ message: "Missing required data for Component." });
}

newsroomHomeData.asuNewsArticles.forEach((article) => {
  if (
    !(
      article?.id &&
      article?.uri &&
      article?.target &&
      article?.text &&
      article?.publishedDate
    )
  ) {
    throw createError({ message: "Missing required data for Component." });
  }
});

newsroomHomeData.socialMediaLinks.forEach((link) => {
  if (!(link?.id && link?.icon && link?.iconAlt && link?.url && link?.text)) {
    throw createError({ message: "Missing required data for Component." });
  }
});

newsroomHomeData.contactCards.forEach((card) => {
  if (!(card?.id && card?.icon && card?.title && card?.text)) {
    throw createError({ message: "Missing required data for Component." });
  }
});

// page metadata setup
definePageMeta({
  layout: false,
  __dangerouslyDisableSanitizersByTagID: {
    "ldjson-schema": ["innerHTML"],
  },
});

// <head> setup
const canonicalURL = UtilityService.formatCanonicalUrl(
  config.public.siteUrl + newsroomHomeData.detailPage
);

useHead({
  title: newsroomHomeData.metaTitle,
  meta: [
    {
      name: "description",
      content: newsroomHomeData.metaDescription,
    },
  ],
  link: [
    {
      rel: "canonical",
      href: canonicalURL,
    },
  ],
  script: [
    useSchemaService().generateNewsroomPageSchema(
      data?.newsroomHome,
      data?.allNewsArticle,
      data?.allNewsCategory
    ),
  ],
});

// featured article setup
const carouselArticles = getFeaturedArticles(newsroomHomeData);

// internal functions
function formatLinkTarget(target: string) {
  return UtilityService.formatLinkTarget(target);
}
function formatDate(date: string) {
  return UtilityService.formatDate(date);
}
function isLastIndex(items: unknown[], index: number) {
  return index === items.length - 1;
}
function handleCarouselCtaClick(event: Event, section: string, text: string) {
  event.preventDefault();

  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    section.toLowerCase(),
    text.toLowerCase(),
    "carousel - image"
  );

  const eventTarget = event.target;

  if (!(eventTarget instanceof HTMLElement)) {
    throw createError({
      message: "Event Target not an instance of HTMLElement.",
    });
  }

  const linkHref = eventTarget.getAttribute("href");
  const linkTarget = eventTarget.getAttribute("target");

  if (!linkHref) {
    throw createError({ message: "Link href required." });
  }

  navigateTo(linkHref, {
    open: {
      target: linkTarget === "_self" ? "_self" : "_blank",
    },
  });
}
function onCarouselChange(section: string, text: string) {
  analyticsComposable.trackSelectEvent(
    "onclick",
    "click",
    "carousel",
    "main content",
    section.toLowerCase(),
    text.toLowerCase(),
    "carousel - image"
  );
}
function handleCardArticleClick(
  event: Event,
  section: string,
  text: string,
  ctaLink: string
) {
  event.preventDefault();

  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    section.toLowerCase(),
    text.toLowerCase()
  );

  navigateTo(ctaLink);
}
function handleInTheNewsClick(section: string, text: string) {
  analyticsComposable.trackLinkEvent(
    "external link",
    "main content",
    section.toLowerCase(),
    text.toLowerCase()
  );
}
function handleCardInfoClick(
  event: Event,
  section: string,
  text: string,
  component: string,
  ctaLink: string
) {
  event.preventDefault();

  analyticsComposable.trackLinkEvent(
    "internal link",
    "main content",
    section.toLowerCase(),
    text.toLowerCase(),
    component.toLowerCase()
  );

  navigateTo(ctaLink);
}

function handleSocialMediaLinkClick(item: ParagraphSocialLink) {
  analyticsComposable.trackLinkEvent(
    "external link",
    "main content",
    "social channels",
    item?.iconAlt?.toLowerCase() ?? ""
  );
}

function handleEnterKeyEvent(link: string) {
  navigateTo(link);
}

function getFeaturedArticles(newsroomHomeData: NewsroomHome) {
  if (
    !(
      newsroomHomeData.firstFeaturedArticle &&
      newsroomHomeData.secondFeaturedArticle &&
      newsroomHomeData.thirdFeaturedArticle &&
      newsroomHomeData.fourthFeaturedArticle
    )
  ) {
    throw createError({
      message: "Missing one or more Featured Newsroom Articles.",
    });
  }

  const carouselArticles: CarouselItem[] = [
    {
      imageSource: newsroomHomeData.firstFeaturedArticle.coverImage ?? "",
      title: newsroomHomeData.firstFeaturedArticle.title ?? "",
      text: newsroomHomeData.firstFeaturedArticle.shortDescription ?? "",
      displayCta: true,
      ctaLink: newsroomHomeData.firstFeaturedArticle.detailPage ?? "",
      ctaLinkTarget: "_self",
      ctaText: "Keep reading",
    },
    {
      imageSource: newsroomHomeData.secondFeaturedArticle.coverImage ?? "",
      title: newsroomHomeData.secondFeaturedArticle.title ?? "",
      text: newsroomHomeData.secondFeaturedArticle.shortDescription ?? "",
      displayCta: true,
      ctaLink: newsroomHomeData.secondFeaturedArticle.detailPage ?? "",
      ctaLinkTarget: "_self",
      ctaText: "Keep reading",
    },
    {
      imageSource: newsroomHomeData.thirdFeaturedArticle.coverImage ?? "",
      title: newsroomHomeData.thirdFeaturedArticle.title ?? "",
      text: newsroomHomeData.thirdFeaturedArticle.shortDescription ?? "",
      displayCta: true,
      ctaLink: newsroomHomeData.thirdFeaturedArticle.detailPage ?? "",
      ctaLinkTarget: "_self",
      ctaText: "Keep reading",
    },
    {
      imageSource: newsroomHomeData.fourthFeaturedArticle.coverImage ?? "",
      title: newsroomHomeData.fourthFeaturedArticle.title ?? "",
      text: newsroomHomeData.fourthFeaturedArticle.shortDescription ?? "",
      displayCta: true,
      ctaLink: newsroomHomeData.fourthFeaturedArticle.detailPage ?? "",
      ctaLinkTarget: "_self",
      ctaText: "Keep reading",
    },
  ];

  // ensure that the dynamically set fields are present
  carouselArticles.forEach((article) => {
    if (
      !(article.imageSource && article.title && article.text && article.ctaLink)
    ) {
      throw createError({
        message:
          "One or more dynamic properties missing for Newsroom's Featured Carousel Articles.",
      });
    }
  });

  return carouselArticles;
}
</script>

<style lang="scss" scoped>
.asu-news-link {
  color: var(--rds-dark-3);
}

.asu-news-link:hover {
  color: var(--rds-primary);
}

.title-underline {
  width: 64px;
  height: 8px;
}

.social-media-image-div {
  top: -48px;
}

.social-media-image {
  height: 303px;
}

.social-media-icon:hover {
  transform: scale(1.3);
  transition: transform 0.3s ease-out;
}

.social-media-icon:focus {
  outline: 2px solid var(--rds-light-2);
}

:deep(.card-icon-height) {
  height: 54px;
  width: 54px;
}

.bg-image {
  background-repeat: repeat-y;
  background-size: 100%;
}

@media (min-width: 768px) {
  .social-media-image {
    height: 498px;
  }

  .bg-image {
    background-size: auto 50%;
    background-repeat: repeat;
  }
}

@media (min-width: 992px) {
  .social-media-image-div {
    position: absolute;
    top: -64px;
  }
}

#newsroom-hero-section {
  background-image: url("https://live-asuocms.ws.asu.edu/sites/default/files/dark%20background%20pattern%402x.jpg");
}
</style>
